class BannerCarousel extends HTMLElement {
  constructor() {
    super();
    this.slidesContainer = this.querySelector('.banner-carousel__slides-container');
    this.track = this.querySelector('.banner-carousel__track');
    this.slides = this.querySelectorAll('.banner-carousel__slide');
    this.bullets = this.querySelectorAll('.banner-carousel__pagination-bullet');
    this.videos = this.querySelectorAll('.banner-carousel__video');
    this.buttons = this.querySelectorAll('.mt-btn');

    this.autoplay = this.slidesContainer?.dataset?.autoplay === 'true';
    this.delay = parseInt(this.slidesContainer?.dataset?.delay) || 7000;
    this.fadeEffect = true;

    this.currentIndex = 0;
    this.isTransitioning = false;
    this.autoplayTimer = null;
    this.isDragging = false;
    this.startPos = 0;
    this.startPosY = 0;
    this.currentTranslate = 0;
    this.prevTranslate = 0;

    this.goToSlide = this.goToSlide.bind(this);
    this.nextSlide = this.nextSlide.bind(this);
    this.prevSlide = this.prevSlide.bind(this);
    this.handleTouchStart = this.handleTouchStart.bind(this);
    this.handleTouchMove = this.handleTouchMove.bind(this);
    this.handleTouchEnd = this.handleTouchEnd.bind(this);
    this.handleMouseDown = this.handleMouseDown.bind(this);
    this.handleMouseMove = this.handleMouseMove.bind(this);
    this.handleMouseUp = this.handleMouseUp.bind(this);
    this.startAutoplay = this.startAutoplay.bind(this);
    this.stopAutoplay = this.stopAutoplay.bind(this);

    this.init();
  }

  disconnectedCallback() {
    this.cleanup();
  }

  init() {
    if (this.slides.length <= 1) return;

    this.setupSlides();
    this.attachEventListeners();
    this.initializeVideos();
    this.setupButtonTracking();
    
    if (this.autoplay) {
      this.startAutoplay();
    }

    this.updateSlideVisibility();
  }

  setupSlides() {
    this.slides.forEach((slide, index) => {
      slide.style.position = 'absolute';
      slide.style.top = '0';
      slide.style.left = '0';
      slide.style.width = '100%';
      slide.style.height = '100%';
      slide.style.opacity = index === 0 ? '1' : '0';
      slide.style.transition = 'opacity 300ms ease-in-out';
      slide.style.zIndex = index === 0 ? '1' : '0';

      const interactiveElements = slide.querySelectorAll('a, button');
      interactiveElements.forEach(el => {
        el.setAttribute('tabindex', index === 0 ? '0' : '-1');
      });
    });

    if (this.track) {
      this.track.style.position = 'relative';
      this.track.style.width = '100%';
      this.track.style.height = '100%';
    }
  }

  attachEventListeners() {
    this.bullets.forEach((bullet, index) => {
      bullet.addEventListener('click', () => this.goToSlide(index));
      bullet.addEventListener('keydown', (e) => this.handleBulletKeydown(e, index));
    });

    if (this.track) {
      this.track.addEventListener('touchstart', this.handleTouchStart, { passive: false });
      this.track.addEventListener('touchmove', this.handleTouchMove, { passive: false });
      this.track.addEventListener('touchend', this.handleTouchEnd);

      this.track.addEventListener('mousedown', this.handleMouseDown);
      window.addEventListener('mousemove', this.handleMouseMove);
      window.addEventListener('mouseup', this.handleMouseUp);
    }

    this.addEventListener('mouseenter', this.stopAutoplay);
    this.addEventListener('mouseleave', () => {
      if (this.autoplay) this.startAutoplay();
    });

    this.slides.forEach(slide => {
      const interactiveElements = slide.querySelectorAll('a, button, [tabindex="0"]');
      interactiveElements.forEach(element => {
        element.addEventListener('focus', () => {
          this.stopAutoplay();
        });
        element.addEventListener('blur', () => {
          setTimeout(() => {
            const focusedElement = document.activeElement;
            const isStillInSlide = slide.contains(focusedElement);
            if (!isStillInSlide && this.autoplay) {
              this.startAutoplay();
            }
          }, 100);
        });
      });
    });

    document.addEventListener('visibilitychange', () => {
      if (document.hidden) {
        this.stopAutoplay();
      } else if (this.autoplay) {
        this.startAutoplay();
      }
    });
  }

  initializeVideos() {
    this.videos.forEach((video, index) => {
      video.addEventListener('loadeddata', () => {
        video.classList.add('is-visible');
      });

      if (index === this.currentIndex) {
        video.classList.add('is-visible');
        video.currentTime = 0;
        video.play().catch(() => {
          
        });
      } else {
        video.pause();
      }
    });
  }

  setupButtonTracking() {
    this.buttons.forEach(btn => {
      const slideHeader = btn.closest('.banner-carousel__content')?.querySelector('.banner-carousel__header');
      if (!slideHeader) return;
      btn.addEventListener('click', () => app.dl.ctaClick(slideHeader.innerText, { location: 'Hero' }));
    });
  }

  goToSlide(index) {
    if (index === this.currentIndex || this.isTransitioning || index < 0 || index >= this.slides.length) {
      return;
    }

    this.isTransitioning = true;
    this.stopAutoplay();

    const currentSlide = this.slides[this.currentIndex];
    const nextSlide = this.slides[index];

    this.handleVideoTransition(this.currentIndex, index);

    const currentInteractiveElements = currentSlide.querySelectorAll('a, button');
    const nextInteractiveElements = nextSlide.querySelectorAll('a, button');

    currentSlide.style.opacity = '0';
    currentSlide.style.zIndex = '0';
    currentSlide.setAttribute('aria-hidden', 'true');
    currentInteractiveElements.forEach(el => {
      el.setAttribute('tabindex', '-1');
    });
    
    nextSlide.style.opacity = '1';
    nextSlide.style.zIndex = '1';
    nextSlide.removeAttribute('aria-hidden');
    nextInteractiveElements.forEach(el => {
      el.setAttribute('tabindex', '0');
    });

    this.updatePagination(index);

    this.currentIndex = index;

    setTimeout(() => {
      this.isTransitioning = false;
      if (this.autoplay) {
        this.startAutoplay();
      }
    }, 300);
  }

  handleVideoTransition(fromIndex, toIndex) {
    this.slides.forEach((slide, index) => {
      const videos = slide.querySelectorAll('.banner-carousel__video');
      videos.forEach(video => {
        if (!video.paused) {
          video.pause();
        }
        video.currentTime = 0;
        if (index !== toIndex) {
          video.classList.remove('is-visible');
        }
      });
    });

    const targetSlide = this.slides[toIndex];
    const targetVideos = targetSlide.querySelectorAll('.banner-carousel__video');
    targetVideos.forEach(video => {
      if (video.readyState === 0) {
        video.load();
      }
      
      video.classList.add('is-visible');
      
      setTimeout(() => {
        video.play().catch(() => {
          
        });
      }, 100);
    });
  }

  updatePagination(activeIndex) {
    this.bullets.forEach((bullet, index) => {
      const isActive = index === activeIndex;
      bullet.classList.toggle('banner-carousel__pagination-bullet--active', isActive);
      bullet.setAttribute('aria-selected', isActive ? 'true' : 'false');
      bullet.setAttribute('tabindex', isActive ? '0' : '-1');
    });
  }

  updateSlideVisibility() {
    this.slides.forEach((slide, index) => {
      const isActive = index === this.currentIndex;
      const interactiveElements = slide.querySelectorAll('a, button');
      
      if (isActive) {
        slide.style.opacity = '1';
        slide.style.zIndex = '1';
        slide.removeAttribute('aria-hidden');
        interactiveElements.forEach(el => {
          el.setAttribute('tabindex', '0');
        });
      } else {
        slide.style.opacity = '0';
        slide.style.zIndex = '0';
        slide.setAttribute('aria-hidden', 'true');
        interactiveElements.forEach(el => {
          el.setAttribute('tabindex', '-1');
        });
      }
    });
    this.updatePagination(this.currentIndex);
  }

  nextSlide() {
    const nextIndex = (this.currentIndex + 1) % this.slides.length;
    this.goToSlide(nextIndex);
  }

  prevSlide() {
    const prevIndex = this.currentIndex === 0 ? this.slides.length - 1 : this.currentIndex - 1;
    this.goToSlide(prevIndex);
  }

  handleBulletKeydown(event, currentBulletIndex) {
    let targetIndex = currentBulletIndex;

    switch(event.key) {
      case 'ArrowLeft':
      case 'ArrowUp':
        event.preventDefault();
        targetIndex = currentBulletIndex === 0 ? this.bullets.length - 1 : currentBulletIndex - 1;
        break;
      case 'ArrowRight':
      case 'ArrowDown':
        event.preventDefault();
        targetIndex = (currentBulletIndex + 1) % this.bullets.length;
        break;
      case 'Home':
        event.preventDefault();
        targetIndex = 0;
        break;
      case 'End':
        event.preventDefault();
        targetIndex = this.bullets.length - 1;
        break;
      default:
        return;
    }

    this.goToSlide(targetIndex);
    this.bullets[targetIndex].focus();
  }

  startAutoplay() {
    if (!this.autoplay || this.slides.length <= 1) return;
    
    this.stopAutoplay();

    const currentSlide = this.slides[this.currentIndex];
    const slideDelay = currentSlide.dataset.slideDelay;
    const delay = slideDelay ? parseInt(slideDelay) : this.delay;

    this.autoplayTimer = setTimeout(() => {
      this.nextSlide();
    }, delay);
  }

  stopAutoplay() {
    if (this.autoplayTimer) {
      clearTimeout(this.autoplayTimer);
      this.autoplayTimer = null;
    }
  }

  handleTouchStart(e) {
    if (this.isTransitioning) return;
    
    this.isDragging = true;
    this.startPos = e.touches[0].clientX;
    this.startPosY = e.touches[0].clientY;
    this.stopAutoplay();
  }

  handleTouchMove(e) {
    if (!this.isDragging || this.isTransitioning) return;
    
    const currentPos = e.touches[0].clientX;
    const currentPosY = e.touches[0].clientY;
    const diffX = currentPos - this.startPos;
    const diffY = currentPosY - this.startPosY;
    
    if (Math.abs(diffX) > Math.abs(diffY) && Math.abs(diffX) > 10) {
      e.preventDefault();
    }
  }

  handleTouchEnd(e) {
    if (!this.isDragging || this.isTransitioning) return;
    
    const endPos = e.changedTouches[0].clientX;
    const diff = endPos - this.startPos;
    const threshold = 50;

    this.isDragging = false;

    if (Math.abs(diff) > threshold) {
      if (diff > 0) {
        this.prevSlide();
      } else {
        this.nextSlide();
      }
    } else {
      if (this.autoplay) {
        this.startAutoplay();
      }
    }
  }

  handleMouseDown(e) {
    if (this.isTransitioning) return;
    
    this.isDragging = true;
    this.startPos = e.clientX;
    this.stopAutoplay();
    e.preventDefault();
  }

  handleMouseMove(e) {
    if (!this.isDragging || this.isTransitioning) return;
    e.preventDefault();
  }

  handleMouseUp(e) {
    if (!this.isDragging || this.isTransitioning) return;
    
    const endPos = e.clientX;
    const diff = endPos - this.startPos;
    const threshold = 50;

    this.isDragging = false;

    if (Math.abs(diff) > threshold) {
      if (diff > 0) {
        this.prevSlide();
      } else {
        this.nextSlide();
      }
    } else {
      if (this.autoplay) {
        this.startAutoplay();
      }
    }
  }

  cleanup() {
    this.stopAutoplay();
    
    window.removeEventListener('mousemove', this.handleMouseMove);
    window.removeEventListener('mouseup', this.handleMouseUp);
    document.removeEventListener('visibilitychange', this.handleVisibilityChange);
    
    this.videos.forEach(video => {
      if (!video.paused) {
        video.pause();
      }
    });
  }
}

customElements.define('banner-carousel', BannerCarousel);